
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild,

} from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { filter, finalize, map, Observable, of, switchMap, take, throwError, timer } from 'rxjs';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import {
  AcademicTermModel,
  KkuWorkloadSyncErrorRow,
  KkuWorkloadSyncModel,
  KkuWorkloadSyncResponse,
  StudyPeriodPayLoad,
} from 'src/app/services/models/study-period/study-period.model';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { LoadingDialogComponent } from 'src/shared/loading-dialog/loading-dialog.component';
import { ErrorMessage } from 'src/utils/error-message';
import { getSafeErrorText } from 'src/utils/http-error-message';
import { SetError } from 'src/utils/set-error.validator';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};
const SYNC_WORKLOAD_POLL_INTERVAL_MS = 2500;

interface DateRangeValue {
  startValue: Date | null;
  endValue: Date | null;
};

export type AcademicTermSaveAction = 'create' | 'edit';

export interface AcademicTermSaveFailedEvent {
  action: AcademicTermSaveAction;
  message: string;
}

export interface AcademicTermSyncFailedEvent {
  message: string;
}

interface SyncWorkloadFailure {
  message: string;
  sync?: KkuWorkloadSyncModel | null;
}

@Component({
  selector: 'app-form-add-academic-term',
  imports: [TranslateModule, SharedModule, ReactiveFormsModule, CommonModule],
  templateUrl: './form-add-academic-term.component.html',
  styleUrl: './form-add-academic-term.component.scss',
})
export class FormAddAcademicTermComponent implements AfterViewInit, OnChanges {
  @ViewChild('saveAcademicTermConfirmDialog')
  saveAcademicTermConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('syncWorkloadConfirmDialog')
  syncWorkloadConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('syncWorkloadLoadingDialog')
  syncWorkloadLoadingDialog!: LoadingDialogComponent;

  @Input() academicTermId?: number;

  @Output() saved = new EventEmitter<AcademicTermSaveAction>();
  @Output() saveFailed = new EventEmitter<AcademicTermSaveFailedEvent>();
  @Output() synced = new EventEmitter<void>();
  @Output() syncFailed = new EventEmitter<AcademicTermSyncFailedEvent>();
  @Output() closed = new EventEmitter<void>();

  cdr = inject(ChangeDetectorRef)
  fb = inject(FormBuilder);
  studyPeriodService = inject(StudyPeriodService);
  erros: Partial<any> = {}

  isSaving = false;
  isSyncing = false;
  academicTermSyncAt: string | null = null;
  syncErrorMessage = 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง';
  syncErrorRows: KkuWorkloadSyncErrorRow[] = [];
  yearOptions = this.buildYearOptions();

  form = this.fb.group({
    term: [null as string | null, Validators.required],
    year: [null as string | null, Validators.required],

    studyPeriod: [
      { startValue: null, endValue: null } as DateRangeValue,
      this.dateRangeRequired(),
    ],

    midtermPeriod: [
      { startValue: null, endValue: null } as DateRangeValue,
      this.dateRangeRequired(),
    ],

    finalPeriod: [
      { startValue: null, endValue: null } as DateRangeValue,
      this.dateRangeRequired(),
    ],
  });

  private loadingAcademicTermId?: number;

  constructor() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.valueChanges.subscribe(() => {
        this.clearResolvedError(key);
      });
    });
  }

  openModal(academicTermId?: number): void {
    this.academicTermId = academicTermId;

    if (academicTermId != null) {
      this.loadAcademicTermDetail(academicTermId);
    } else {
      this.clearForm();
    }

    const modalEl = document.getElementById('addNewAcademicTerm');

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  ngAfterViewInit(): void {
    const modalEl = document.getElementById('addNewAcademicTerm');

    modalEl?.addEventListener('hidden.bs.modal', () => {
      this.clearForm();
      this.closed.emit();
    });
  }

  ngOnChanges(): void {
    if (this.academicTermId == null) {
      return;
    }

    this.loadAcademicTermDetail(this.academicTermId);
  }

  onSave(): void {
    this.erros = {}
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {

      this.form.markAllAsTouched();
      this.erros = this.mapErrorsToMessages(SetError(this.form));


      Object.values(this.form.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });

      return;
    }

    this.saveAcademicTermConfirmDialog.open();
  }

  onConfirmSave(): void {
    this.erros = {}
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {

      this.form.markAllAsTouched();
      this.erros = this.mapErrorsToMessages(SetError(this.form));


      Object.values(this.form.controls).forEach(control => {
        control.markAsTouched();
        control.updateValueAndValidity();
      });

      this.saveAcademicTermConfirmDialog.close();

      return;
    }

    this.isSaving = true;

    const action = this.getSaveAction();
    const payload = this.buildPayload();
    const request$ = this.getSaveRequest(payload);

    request$.subscribe({
      next: () => {
        this.saveAcademicTermConfirmDialog.close();
        this.saved.emit(action);
        this.closeModal();
      },
      error: (err: any) => {
        const serverErrors = err.error?.errors ?? err.error ?? {};

        this.erros = this.mapErrorsToMessages(this.mapServerErrors(serverErrors));
        this.saveAcademicTermConfirmDialog.close();
        this.saveFailed.emit({
          action,
          message: this.getSaveErrorMessage(err, action),
        });
        this.cdr.detectChanges()
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      },
    });
  }

  onSyncWorkload(): void {
    const academicTermId = this.academicTermId;

    if (!this.canSyncWorkload() || academicTermId == null) {
      return;
    }

    this.syncWorkloadConfirmDialog.open();
  }

  onConfirmSyncWorkload(): void {
    const academicTermId = this.academicTermId;

    if (!this.canSyncWorkload() || academicTermId == null) {
      this.syncWorkloadConfirmDialog.close();
      return;
    }

    this.isSyncing = true;
    this.syncErrorRows = [];
    this.syncErrorMessage = 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง';
    this.syncWorkloadConfirmDialog.close();
    this.syncWorkloadLoadingDialog.open();

    this.studyPeriodService.syncWorkload(academicTermId).pipe(
      switchMap((res) => this.waitForSyncWorkloadCompletion(res, academicTermId)),
      finalize(() => {
        this.isSyncing = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (res) => {
        this.syncWorkloadLoadingDialog.close(() => {
          this.academicTermSyncAt = res.sync?.academic_term?.sync_at ?? new Date().toISOString();
          this.synced.emit();
        });
      },
      error: (err: unknown) => {
        this.syncWorkloadLoadingDialog.close(() => {
          this.openSyncWorkloadErrorModal(this.normalizeSyncWorkloadFailure(err));
        });
      },
    });
  }

  closeSyncWorkloadErrorModal(): void {
    const modalElement = document.getElementById('syncWorkloadError');
    if (modalElement == null) {
      return;
    }

    this.blurFocusedElementInside(modalElement);
    bootstrap.Modal.getInstance(modalElement)?.hide();
  }

  downloadSyncWorkloadErrorFile(): void {
    if (this.academicTermId == null) {
      return;
    }

    const { year, term } = this.form.getRawValue();
    const yearLabel = year ?? '';
    const termLabel = term ?? '';

    this.studyPeriodService.downloadSyncWorkloadErrors(this.academicTermId).subscribe({
      next: (blob) => this.downloadBlob(
        blob,
        `kku_workload_sync_errors_${yearLabel}_${termLabel}.xlsx`
      ),
      error: () => {
        this.syncFailed.emit({
          message: 'ไม่สามารถดาวน์โหลดไฟล์ error ได้ กรุณาลองใหม่อีกครั้ง',
        });
      },
    });
  }

  canSyncWorkload(): boolean {
    return this.academicTermId != null &&
      this.loadingAcademicTermId == null &&
      !this.isSaving &&
      !this.isSyncing &&
      !this.isAcademicTermSynced();
  }

  isAcademicTermSynced(): boolean {
    return this.academicTermSyncAt != null && this.academicTermSyncAt !== '';
  }

  getAcademicTermSyncAtLabel(): string {
    if (this.academicTermSyncAt == null || this.academicTermSyncAt === '') {
      return '';
    }

    const date = new Date(this.academicTermSyncAt);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const thaiMonths = [
      'มกราคม',
      'กุมภาพันธ์',
      'มีนาคม',
      'เมษายน',
      'พฤษภาคม',
      'มิถุนายน',
      'กรกฎาคม',
      'สิงหาคม',
      'กันยายน',
      'ตุลาคม',
      'พฤศจิกายน',
      'ธันวาคม',
    ];
    const day = date.getDate();
    const month = thaiMonths[date.getMonth()];
    const year = this.toBuddhistYear(date.getFullYear());
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `นำเข้าข้อมูลเมื่อวันที่ ${day} ${month} ${year} เวลา ${hours}:${minutes}:${seconds} น.`;
  }

  clearForm(): void {
    this.academicTermId = undefined;
    this.academicTermSyncAt = null;
    this.isSyncing = false;

    this.form.get('term')?.enable();
    this.form.get('year')?.enable();

    this.form.reset({
      term: null,
      year: String(this.getCurrentBuddhistYear()),
      studyPeriod: null,
      midtermPeriod: null,
      finalPeriod: null,
    });

    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.erros = {};
  }

  loadAcademicTermDetail(id: number): void {
    if (this.loadingAcademicTermId === id) {
      return;
    }

    this.loadingAcademicTermId = id;

    this.studyPeriodService.getAcademicTermById(id).pipe(
      finalize(() => {
        this.loadingAcademicTermId = undefined;
      })
    ).subscribe({
      next: (res) => {
        this.academicTermSyncAt = res.sync_at ?? null;
        this.form.patchValue({
          term: res.name,
          year: String(this.toBuddhistYear(res.year)),
          studyPeriod: {
            startValue: new Date(res.study_start_date),
            endValue: new Date(res.study_end_date),
          },
          midtermPeriod: {
            startValue: new Date(res.midterm_start_date),
            endValue: new Date(res.midterm_end_date),
          },
          finalPeriod: {
            startValue: new Date(res.final_start_date),
            endValue: new Date(res.final_end_date),
          },
        });

        this.form.get('term')?.disable();
        this.form.get('year')?.disable();
      },
      error: (err) => console.error(err),
    });
  }

  private waitForSyncWorkloadCompletion(
    response: KkuWorkloadSyncResponse,
    academicTermId: number
  ): Observable<KkuWorkloadSyncResponse> {
    if (this.isSyncWorkloadSucceeded(response.sync)) {
      return of(response);
    }

    if (this.isSyncWorkloadFailed(response.sync)) {
      throw this.buildSyncWorkloadFailure(response.sync);
    }

    return timer(0, SYNC_WORKLOAD_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.studyPeriodService.getSyncWorkloadStatus(academicTermId)),
      map((statusResponse) => {
        if (this.isSyncWorkloadFailed(statusResponse.sync)) {
          throw this.buildSyncWorkloadFailure(statusResponse.sync);
        }

        return statusResponse;
      }),
      filter((statusResponse) => this.isSyncWorkloadSucceeded(statusResponse.sync)),
      take(1)
    );
  }

  private isSyncWorkloadSucceeded(sync: KkuWorkloadSyncModel | null | undefined): boolean {
    return sync?.status === 'succeeded';
  }

  private isSyncWorkloadFailed(sync: KkuWorkloadSyncModel | null | undefined): boolean {
    return sync?.status === 'failed';
  }

  private getSyncRecordErrorMessage(sync: KkuWorkloadSyncModel | null | undefined): string {
    const errorMessage = sync?.error_message;

    return errorMessage == null || errorMessage === ''
      ? 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง'
      : errorMessage;
  }

  private buildSyncWorkloadFailure(
    sync: KkuWorkloadSyncModel | null | undefined
  ): Error & SyncWorkloadFailure {
    return Object.assign(
      new Error(this.getSyncRecordErrorMessage(sync)),
      { sync }
    );
  }

  private normalizeSyncWorkloadFailure(error: unknown): SyncWorkloadFailure {
    if (error != null && typeof error === 'object' && 'sync' in error) {
      const failure = error as SyncWorkloadFailure;
      return {
        message: failure.message === ''
          ? this.getSyncRecordErrorMessage(failure.sync)
          : failure.message,
        sync: failure.sync,
      };
    }

    return {
      message: this.getSyncErrorMessage(error),
      sync: null,
    };
  }

  private openSyncWorkloadErrorModal(failure: SyncWorkloadFailure): void {
    this.syncErrorMessage = failure.message;
    this.syncErrorRows = failure.sync?.error_rows ?? [];
    this.cdr.detectChanges();

    const errorModalElement = document.getElementById('syncWorkloadError');
    if (errorModalElement == null) {
      this.syncFailed.emit({ message: failure.message });
      return;
    }

    bootstrap.Modal.getOrCreateInstance(errorModalElement, STATIC_MODAL_OPTIONS).show();
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private blurFocusedElementInside(modalElement: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }
  }
  private formatDate(date: any): string {
    if (date == null || date === '') {
      return '';
    }

    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    if (typeof date === 'string') {
      return new Date(date).toISOString().split('T')[0];
    }

    if ('year' in date && 'month' in date && 'day' in date) {
      const month = String(date.month).padStart(2, '0');
      const day = String(date.day).padStart(2, '0');

      return `${date.year}-${month}-${day}`;
    }

    return '';
  }

  private closeModal(): void {
    const modalElement = document.getElementById('addNewAcademicTerm');

    if (modalElement == null) {
      return;
    }

    const modal = bootstrap.Modal.getInstance(modalElement);
    modal?.hide();
  }

  private getSaveRequest(payload: StudyPeriodPayLoad) {
    if (this.academicTermId == null) {
      return this.studyPeriodService.createStudyPeriod(payload);
    }

    return this.studyPeriodService.updateStudyPeriod(
      this.academicTermId,
      payload
    );
  }

  private getSaveAction(): AcademicTermSaveAction {
    return this.academicTermId == null ? 'create' : 'edit';
  }

  private buildPayload(): StudyPeriodPayLoad {
    const value = this.form.getRawValue();

    return {
      academic_term: {
        name: value.term ?? '',
        year: this.toChristianYear(value.year),

        study_start_date: this.formatDate(
          value.studyPeriod?.startValue ?? null
        ),
        study_end_date: this.formatDate(
          value.studyPeriod?.endValue ?? null
        ),

        midterm_start_date: this.formatDate(
          value.midtermPeriod?.startValue ?? null
        ),
        midterm_end_date: this.formatDate(
          value.midtermPeriod?.endValue ?? null
        ),

        final_start_date: this.formatDate(
          value.finalPeriod?.startValue ?? null
        ),
        final_end_date: this.formatDate(
          value.finalPeriod?.endValue ?? null
        ),
      },
    };

  }

  private mapErrorsToMessages(errors: Record<string, unknown>): Record<string, string> {
    const errorMessage = new ErrorMessage();

    return Object.keys(errors).reduce((result, key) => {
      result[key] = errorMessage.getErrorMessage(errors[key]);

      return result;
    }, {} as Record<string, string>);
  }

  private mapServerErrors(errors: Record<string, unknown>): Record<string, unknown> {
    const keyMap: Record<string, string> = {
      name: 'term',
      study_start_date: 'studyPeriod',
      study_end_date: 'studyPeriod',
      midterm_start_date: 'midtermPeriod',
      midterm_end_date: 'midtermPeriod',
      final_start_date: 'finalPeriod',
      final_end_date: 'finalPeriod',
    };

    return Object.entries(errors).reduce((result, [key, value]) => {
      const mappedKey = keyMap[key] ?? key;

      if (result[mappedKey] == null) {
        result[mappedKey] = value;

        return result;
      }

      const existingMessages = Array.isArray(result[mappedKey])
        ? result[mappedKey] as unknown[]
        : [result[mappedKey]];
      const newMessages = Array.isArray(value) ? value : [value];

      result[mappedKey] = [...existingMessages, ...newMessages];

      return result;
    }, {} as Record<string, unknown>);
  }

  private clearResolvedError(key: string): void {
    const control = this.form.get(key);

    if (control?.valid === true && this.erros[key] != null) {
      delete this.erros[key];
    }
  }

  private dateRangeRequired(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (
        typeof value !== 'object' ||
        value === null ||
        !('startValue' in value) ||
        !('endValue' in value)
      ) {
        return { required: true };
      }

      if (value.startValue == null || value.endValue == null) {
        return { required: true };
      }

      return null;
    };
  }

  private toChristianYear(value: unknown): number {
    const year = Number(value);

    return year > 2400 ? year - 543 : year;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private getCurrentBuddhistYear(): number {
    return this.toBuddhistYear(new Date().getFullYear());
  }

  private buildYearOptions(): string[] {
    const currentYear = this.getCurrentBuddhistYear();

    return [currentYear - 1, currentYear, currentYear + 1].map(String);
  }

  private getSaveErrorMessage(error: unknown, action: AcademicTermSaveAction): string {
    const fallbackMessage = action === 'create'
      ? 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง'
      : 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .map((item) => this.getErrorText(item))
          .filter((item) => item !== '');

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private getSyncErrorMessage(error: unknown): string {
    const fallbackMessage = 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .map((item) => this.getErrorText(item))
          .filter((item) => item !== '');

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return getSafeErrorText(value);
  }
}
